import './style.css';

import { Button, DropdownMenu, MenuItem, View } from '@zinc/ui';
import type DraftStore from 'bz-draft/modules/stores/draft-store';
import type { IToolbar, IToolbarValue } from 'bz-draft/modules/stores/draft-store';
import { Provider } from 'mobx-react';
import React from 'react';
import { EditorState, Entity, Modifier, RichUtils } from 'tm2-draft-js';
import { SelectionBar } from '../../../controls/selection-bar';
import SnippetDialog from './controls/snippet-dialog';
import SnippetDialogStore from './controls/snippet-dialog/stores/dialog-store';

export interface IProps {
  store: DraftStore;
}

class Toolbar extends React.Component<IProps> {
  snippetDialog = new SnippetDialogStore();

  constructor(props: IProps) {
    super(props);
    const { store } = props;

    this.state = {
      customStyles: store.toolbar.map((t, i) => {
        return t.value.map((v) => {
          if (typeof v.style === 'string') {
            return v.style;
          }
          return `${t.prefix}-${v.key}`;
        });
      })
    };
  }

  render() {
    let { store } = this.props;
    const { mode, editor, toolbar } = store;

    if (mode === 'live') {
      return <span />;
    }

    const current = editor.getCurrentInlineStyle();

    const tools = toolbar.map((x, i) => {
      return (
        <Tool
          key={i}
          {...x}
          current={current}
          editor={editor}
          onChange={this.onChange.bind(this, x.type, i)}
        />
      );
    });

    return (
      <Provider draftStore={store}>
        <div>
          <View
            direction="row"
            align="center"
            backgroundColor="page"
            gap={2}
            padding={2}
            className="border-b border-neutral-faded"
          >
            {tools}
            <SelectionBar store={this.props.store} />
          </View>
          <Provider dialogStore={this.snippetDialog}>
            <SnippetDialog />
          </Provider>
        </div>
      </Provider>
    );
  }

  insertSelectedEntity = (editor, entityKey, value) => {
    let next;
    const selection = editor.getSelection();
    const content = editor.getCurrentContent();
    const currentStyle = editor.getCurrentInlineStyle();
    const hasStyle = currentStyle.has(value);
    let toggleStyle = true;
    if (hasStyle) {
      entityKey = Entity.create('span', 'MUTABLE', {});
    } else {
      entityKey = entityKey || Entity.create('span', 'MUTABLE', {});
    }

    if (selection.isCollapsed()) {
      const entity = Modifier.insertText(content, selection, ' ', null, entityKey);
      next = EditorState.push(editor, entity, 'insert-text');

      const _selection = next.getSelection();
      const offset = _selection.getFocusOffset();

      let updated = _selection.merge({
        focusOffset: offset,
        anchorOffset: offset - 1
      });

      updated = updated.set('hasFocus', true);
      next = EditorState.acceptSelection(next, updated);
      toggleStyle = !hasStyle;
    } else {
      const contentWithEntity = Modifier.applyEntity(content, selection, entityKey);
      next = EditorState.push(editor, contentWithEntity, '');
    }

    if (toggleStyle) {
      next = RichUtils.toggleInlineStyle(next, value);
    }

    return next;
  };

  onChange(type: IToolbar['type'], idx: number, value: IToolbarValue['key']) {
    let next;
    const { store } = this.props;
    const { editor } = store;
    let selection;
    switch (type) {
      case 'block':
        selection = editor.getSelection();
        next = RichUtils.toggleBlockType(editor, value);
        break;

      case 'composite':
        selection = editor.getSelection();
        next = RichUtils.toggleInlineStyle(editor, value);
        break;

      case 'dropdown':
        // this might need to use insertSelectedEntity style if ever used
        selection = editor.getSelection();

        const nextContentState = this.state.customStyles[idx].reduce((contentState, x) => {
          return Modifier.removeInlineStyle(contentState, selection, x);
        }, editor.getCurrentContent());

        next = EditorState.push(editor, nextContentState, 'change-inline-style');

        const currentStyle = editor.getCurrentInlineStyle();

        if (selection.isCollapsed()) {
          next = currentStyle.reduce((state, x) => {
            return RichUtils.toggleInlineStyle(state, x);
          }, next);
        }

        if (!currentStyle.has(value)) {
          next = RichUtils.toggleInlineStyle(next, value);
        }
        break;

      case 'header':
        next = this.insertSelectedEntity(
          editor,
          Entity.create(value.toLowerCase(), 'MUTABLE', {}),
          value
        );
        break;

      case 'snippet':
        this.snippetDialog.open = true;
        return;
    }

    return store.onTemplate(next);
  }
}

export interface IToolProps extends IToolbar {
  current: any;
  editor: any;
  onChange: (value: IToolbarValue['key']) => void;
}

class Tool extends React.Component<IToolProps> {
  static defaultProps: Partial<IToolProps> = {
    mimic: false,
    value: [],
    onChange: () => {}
  };
  cache!: React.ReactNode[];

  UNSAFE_componentWillMount = () => {
    if (this.props.type !== 'dropdown') {
      return;
    }

    return (this.cache = this.props.value.map((x, i) => {
      const eventKey = typeof x.style === 'string' ? x.style : `${this.props.prefix}-${x.key}`;
      const style = this.props.mimic && typeof x.style !== 'string' ? x.style : {};

      return (
        <MenuItem key={i} eventKey={eventKey} icon={<i className={`fa ${x.icon}`} />}>
          {x.label}
        </MenuItem>
      );
    }));
  };

  render() {
    let style;
    let composite;
    const { type } = this.props;
    switch (type) {
      case 'composite':
      case 'block':
        const selection = this.props.editor.getSelection();
        const blockType = this.props.editor
          .getCurrentContent()
          .getBlockForKey(selection.getStartKey())
          .getType();
        composite = this.props.value.map((x, i) => {
          const selectStyle =
            typeof x.style === 'string' ? x.style : `${this.props.prefix}-${x.key}`;
          style = this.props.mimic && typeof x.style !== 'string' ? x.style : {};
          return (
            <Button
              key={i}
              variant="faded"
              color={
                this.props.current.has(selectStyle) || blockType === x.style ? 'primary' : 'neutral'
              }
              attributes={{
                onMouseDown: this.onSelect.bind(this, selectStyle),
                onTouchStart: this.onSelect.bind(this, selectStyle)
              }}
              icon={<i style={style} className={`fa ${x.icon}`} />}
            >
              {x.label}
            </Button>
          );
        });

        return <Button.Group>{composite}</Button.Group>;
      case 'dropdown':
        return (
          <DropdownMenu id={`dropdown-${this.props.prefix}`} onSelect={this.onSelect}>
            <DropdownMenu.Trigger>
              {(attributes) => (
                <Button
                  attributes={attributes}
                  variant="faded"
                  icon={<i className={`fa ${this.props.icon}`} />}
                >
                  {this.props.label}
                </Button>
              )}
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>{this.cache}</DropdownMenu.Content>
          </DropdownMenu>
        );

      case 'header':
      case 'snippet':
        composite = this.props.value.map((x, i) => {
          return (
            <Button
              key={i}
              variant="faded"
              color={this.props.current.has(x.style) ? 'primary' : 'neutral'}
              onClick={this.onSelect.bind(this, x.key)}
              icon={<i className={`fa ${x.icon}`} />}
            >
              {x.label}
            </Button>
          );
        });
        return <Button.Group>{composite}</Button.Group>;

      default:
        console.error('[Toolbar] - unknown tool type:', type);
        return null;
    }
  }

  onSelect(style: IToolbarValue['key'], e: React.MouseEvent | React.TouchEvent): void {
    if (e) {
      e.preventDefault();
    }
    this.props.onChange.call(this, style);
  }
}

export default Toolbar;
